/**
 * RegistrationOTPModal
 * Opened after anonymous registration form submission.
 * Matches the AuthModal OTP step design exactly.
 */
import { useState, useEffect } from 'react';
import { X, Check, Loader } from 'lucide-react';
import { registerVerifyOTP, registerVerifyFirebase } from '../api/api';
import { MODAL_BG } from '../assets/modalBg';
import api from '../api/api';
import { track, setAnalyticsUserId } from '../utils/analytics';

const Benefits = () => (
  <div>
    <div className="flex items-center gap-3 my-4">
      <div className="flex-1 border-t border-gray-200" />
    </div>
    <div className="text-center">
      <p className="text-xs text-gray-400 mb-2">Free to Join. Takes 30 seconds.</p>
      <div className="space-y-1">
        {[
          'Discover Interesting Activities',
          'Access Registration Links',
          'Follow Changemakers',
          'Get Updates and Reminders',
        ].map((b) => (
          <p key={b} className="text-xs text-gray-400 flex items-center justify-center gap-1.5">
            <Check size={13} className="text-green-500 flex-shrink-0" />
            {b}
          </p>
        ))}
      </div>
    </div>
  </div>
);

export default function RegistrationOTPModal({
  activityId,
  method,          // 'email' | 'phone'
  contact,         // display string (masked email or phone)
  pendingRegId,
  email,           // actual email for email OTP verification
  confirmResult,   // Firebase ConfirmationResult (phone)
  onSuccess,       // fn(user)
  onClose,
  sourceProps = {},  // { activity_id, organisation_id, organisation_name, source_page }
}) {
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendMsg, setResendMsg] = useState('');
  const [mobileKeyboard, setMobileKeyboard] = useState(false);
  const [otpAttempts, setOtpAttempts] = useState(0);

  const _activityId = sourceProps?.activity_id || activityId;

  // Track OTP modal opened once on mount
  useEffect(() => {
    track('karmana_reg_otp_opened', {
      activity_id: _activityId,
      otp_method: method,
      source_page: sourceProps?.source_page || null,
    });
  }, []); // eslint-disable-line

  // Auto-verify as soon as all 6 digits are entered (matches AuthModal behaviour)
  useEffect(() => {
    if (otp.length === 6 && !verifying) handleVerify();
  }, [otp]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInputFocus = () => { if (window.innerWidth < 768) setMobileKeyboard(true); };
  const handleInputBlur  = () => setMobileKeyboard(false);

  const handleVerify = async () => {
    if (!otp.trim() || otp.length < 6) {
      setError('Enter the 6-digit code');
      return;
    }
    const currentAttempt = otpAttempts + 1;
    setOtpAttempts(currentAttempt);
    track('karmana_reg_otp_verify_click', {
      activity_id: _activityId,
      otp_method: method,
      otp_length: otp.length,
    });
    setError('');
    setVerifying(true);
    try {
      let res;
      if (method === 'phone' && confirmResult) {
        const userCredential = await confirmResult.confirm(otp);
        const idToken = await userCredential.user.getIdToken();
        res = await registerVerifyFirebase(activityId, {
          firebase_id_token: idToken,
          pending_registration_id: pendingRegId,
        });
      } else {
        res = await registerVerifyOTP(activityId, {
          email,
          otp,
          pending_registration_id: pendingRegId,
        });
      }
      // Fire signup_completed — this is the moment a new Karmana user is created
      const completedUser = res.data.user;
      track('signup_completed', {
        method: `karmana_${method}`,
        is_first_time: res.data.is_new_user ?? true,
        user_id: completedUser?.id || null,
        activity_id: _activityId,
        organisation_id: sourceProps?.organisation_id || null,
        trigger: 'karmana_registration',
        source_page: sourceProps?.source_page || null,
      });
      // Set pending onboarding flag for new users (success modal close will trigger it)
      if (res.data.is_new_user) {
        try { sessionStorage.setItem('karmana_new_user_onboarding', '1'); } catch { /* ignore */ }
      }
      setAnalyticsUserId(completedUser?.id || null);
      onSuccess(completedUser);
    } catch (err) {
      track('karmana_reg_otp_failed', {
        activity_id: _activityId,
        otp_method: method,
        attempt_number: currentAttempt,
      });
      setError(err?.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resending || method !== 'email') return;
    setResending(true);
    setResendMsg('');
    setError('');
    try {
      await api.post(`/activities/${activityId}/resend-otp`, { email });
      track('karmana_reg_otp_resend', { activity_id: _activityId, otp_method: method });
      setResendMsg('A new OTP has been sent to your email.');
    } catch (err) {
      setResendMsg(err?.response?.data?.detail || 'Could not resend. Please try again.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-[200] flex justify-center bg-black/50 p-4 transition-all duration-200 ${
        mobileKeyboard ? 'items-start pt-24' : 'items-center'
      }`}
      data-testid="reg-otp-modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative rounded-2xl shadow-xl w-full max-w-sm md:max-w-[624px] p-6 overflow-hidden"
        style={{
          backgroundColor: '#f7f5f0',
          backgroundImage: `url(${MODAL_BG})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-300 hover:text-gray-500 transition-colors z-10"
          data-testid="reg-otp-modal-close"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Heading */}
        <div className="text-center mb-4 mt-2">
          <h2 className="text-2xl font-bold text-gray-900">You're Almost There</h2>
          <p className="text-gray-700 text-base mt-1 font-semibold">Continue to Join Karmana</p>
        </div>

        {/* OTP description */}
        <p className="text-sm text-gray-600 mb-4">
          Enter the 6-digit code sent to{' '}
          <span className="font-semibold text-gray-800">{contact}</span>
        </p>

        {/* OTP input */}
        <input
          type="text"
          inputMode="numeric"
          maxLength={6}
          value={otp}
          onChange={e => { setOtp(e.target.value.replace(/\D/g, '').slice(0, 6)); setError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleVerify()}
          placeholder="000000"
          autoFocus
          data-testid="reg-otp-modal-input"
          className="w-full text-center text-2xl tracking-[0.5em] border border-gray-300 rounded-lg py-3 focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
        />

        {/* Verify button */}
        <button
          onClick={handleVerify}
          disabled={verifying || otp.length < 6}
          data-testid="reg-otp-modal-verify-btn"
          className={`w-full mt-3 py-3 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
            !verifying && otp.length >= 6
              ? 'bg-green-600 text-white hover:bg-green-700'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-60'
          }`}
        >
          {verifying ? (
            <><Loader size={15} className="animate-spin" />Verifying…</>
          ) : (
            'Verify & Log In'
          )}
        </button>

        {/* Error */}
        {error && (
          <p className="text-red-500 text-sm text-center mt-2" data-testid="reg-otp-error">
            {error}
          </p>
        )}

        {/* Resend */}
        {method === 'email' && (
          <p className="text-xs text-center text-gray-400 mt-3">
            Didn&apos;t receive the code?{' '}
            <button
              onClick={handleResend}
              disabled={resending}
              data-testid="reg-otp-resend-btn"
              className="text-green-700 font-semibold hover:underline disabled:opacity-50"
            >
              {resending ? 'Sending…' : 'Resend OTP'}
            </button>
          </p>
        )}
        {resendMsg && (
          <p className="text-xs text-center text-green-700 font-medium mt-1">{resendMsg}</p>
        )}

        {/* Benefits */}
        <Benefits />

        {/* Karmana logo */}
        <div className="flex items-center justify-center gap-2 mt-5 pt-3 border-t border-gray-200/60">
          <img
            src="/images/logo.webp"
            alt="Karmana"
            className="w-8 h-8 rounded-full object-contain"
          />
          <div className="text-left">
            <p className="text-sm font-bold text-gray-900 leading-tight">Karmana</p>
            <p className="text-[10px] text-gray-500 leading-tight">Bengaluru&apos;s Civic Action Platform</p>
          </div>
        </div>
      </div>
    </div>
  );
}
